import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Drawer, Select, Space, Spin, message } from "antd";
import { fetchHealthClientUslPay, postHealthClientUslPay } from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const DRAWER_WIDTH_KEY = "planOnline.health.clients.payDrawer.width";
const MIN_WIDTH = 420;
const MAX_WIDTH = 640;
const DEFAULT_WIDTH = 480;

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const formatMoney = (value) => {
  const n = toNumber(value);
  if (n == null) return "0,00";
  return n.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const normalizePayItem = (row) => {
  const qtyOrigin = toNumber(pick(row, "qty", "Qty")) ?? 1;
  return {
    idStr: toNumber(pick(row, "idStr", "IdStr")),
    idUsl: toNumber(pick(row, "idUsl", "IdUsl")),
    name: pick(row, "name", "Name") ?? "",
    cnt: toNumber(pick(row, "cnt", "Cnt")) ?? 0,
    qtyOrigin: qtyOrigin < 1 ? 1 : qtyOrigin,
    zena: toNumber(pick(row, "zena", "Zena")) ?? 0,
    syma: toNumber(pick(row, "syma", "Syma")) ?? 0,
    symaDolg: toNumber(pick(row, "symaDolg", "SymaDolg")) ?? 0
  };
};

const normalizeKassa = (row) => ({
  id: toNumber(pick(row, "id", "Id")),
  name: pick(row, "name", "Name") ?? ""
});

/**
 * Оплата платных назначений (Delphi TFrmSprClientsPay). Касса «Внешняя PMS» — начисление на счёт гостя ECVI.
 */
const ClientAppointmentPayDrawer = ({ open, idClient, onClose, onDone }) => {
  const [size, setSize] = useState(loadWidth);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [kassas, setKassas] = useState([]);
  const [idKassa, setIdKassa] = useState(null);
  const [checked, setChecked] = useState([]);
  const [qtyByStr, setQtyByStr] = useState({});
  const reqRef = useRef(0);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  useEffect(() => {
    if (!open || !idClient) return;
    const requestId = ++reqRef.current;
    setLoading(true);
    setError("");
    setSubmitting(false);

    fetchHealthClientUslPay(idClient)
      .then((data) => {
        if (requestId !== reqRef.current) return;
        const list = Array.isArray(data?.items ?? data?.Items)
          ? (data.items ?? data.Items).map(normalizePayItem).filter((x) => x.idStr != null)
          : [];
        const kassaList = Array.isArray(data?.kassas ?? data?.Kassas)
          ? (data.kassas ?? data.Kassas).map(normalizeKassa).filter((x) => x.id != null)
          : [];
        const defKassa = toNumber(pick(data, "defaultIdKassa", "DefaultIdKassa"));
        setItems(list);
        setKassas(kassaList);
        setChecked(list.map((x) => x.idStr));
        const qtyMap = {};
        list.forEach((x) => {
          qtyMap[x.idStr] = x.qtyOrigin;
        });
        setQtyByStr(qtyMap);
        const preferred =
          defKassa > 0 && kassaList.some((k) => k.id === defKassa) ? defKassa : kassaList[0]?.id ?? null;
        setIdKassa(preferred);
      })
      .catch((e) => {
        if (requestId !== reqRef.current) return;
        setError(e.message || "Не удалось загрузить долг");
        setItems([]);
      })
      .finally(() => {
        if (requestId === reqRef.current) setLoading(false);
      });
  }, [open, idClient]);

  const selected = useMemo(
    () => items.filter((x) => checked.includes(x.idStr)),
    [items, checked]
  );

  const total = useMemo(
    () =>
      selected.reduce((sum, item) => {
        if (item.symaDolg < 0) return sum + item.syma;
        const qty = qtyByStr[item.idStr] ?? item.qtyOrigin;
        return sum + item.zena * qty;
      }, 0),
    [selected, qtyByStr]
  );

  const isReturn = total < -0.005;

  const kassaOptions = useMemo(
    () => kassas.map((k) => ({ value: k.id, label: k.name || `#${k.id}` })),
    [kassas]
  );

  const toggleItem = (idStr, value) => {
    setChecked((prev) => {
      if (value) return prev.includes(idStr) ? prev : [...prev, idStr];
      return prev.filter((x) => x !== idStr);
    });
  };

  const changeQty = (item, delta) => {
    if (item.symaDolg < 0) return;
    setQtyByStr((prev) => {
      const current = prev[item.idStr] ?? item.qtyOrigin;
      const next = Math.min(item.qtyOrigin, Math.max(1, current + delta));
      return { ...prev, [item.idStr]: next };
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const onSubmit = () => {
    if (!selected.length) {
      message.error("Выберите услуги");
      return;
    }
    if (!idKassa) {
      message.error("Выберите кассу");
      return;
    }

    setSubmitting(true);
    setError("");
    postHealthClientUslPay(idClient, {
      idKassa,
      items: selected.map((item) => ({
        idStr: item.idStr,
        qty: item.symaDolg < 0 ? item.qtyOrigin : qtyByStr[item.idStr] ?? item.qtyOrigin
      }))
    })
      .then(() => {
        message.success(isReturn ? "Возврат выполнен" : "Оплата выполнена");
        onDone?.();
      })
      .catch((e) => {
        setError(e.message || "Не удалось выполнить оплату");
      })
      .finally(() => setSubmitting(false));
  };

  const footer = (
    <div className="client-appointment-pay__footer">
      <div className="client-appointment-pay__total">
        Сумма: <b>{formatMoney(total)}</b>
      </div>
      <Space>
        <Button
          type="primary"
          onClick={onSubmit}
          loading={submitting}
          disabled={loading || !selected.length || !idKassa}
        >
          {isReturn ? "Возврат" : "Оплата"}
        </Button>
        <Button onClick={handleClose} disabled={submitting}>
          Отмена
        </Button>
      </Space>
    </div>
  );

  return (
    <Drawer
      placement="right"
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={handleClose}
      destroyOnClose
      title="Оплата назначений"
      rootClassName="client-appointment-pay"
      footer={footer}
      maskClosable={!submitting}
      keyboard={!submitting}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {loading ? (
        <div className="client-appointment-pay__center">
          <Spin tip="Загрузка…" />
        </div>
      ) : (
        <div className="client-appointment-pay__body">
          {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

          <div className="client-appointment-pay__kassa">
            <div className="client-appointment-pay__label">Касса</div>
            <Select
              value={idKassa}
              options={kassaOptions}
              onChange={setIdKassa}
              placeholder="Выберите кассу"
              style={{ width: "100%" }}
              disabled={submitting}
            />
          </div>

          <div className="client-appointment-pay__list">
            {items.length ? (
              items.map((item) => {
                const qty = qtyByStr[item.idStr] ?? item.qtyOrigin;
                const line = item.symaDolg < 0 ? item.syma : item.zena * qty;
                const canQty = item.symaDolg >= 0 && item.qtyOrigin > 1;
                return (
                  <div key={item.idStr} className="client-appointment-pay__item">
                    <Checkbox
                      checked={checked.includes(item.idStr)}
                      disabled={submitting}
                      onChange={(e) => toggleItem(item.idStr, e.target.checked)}
                    >
                      <span className="client-appointment-pay__name">{item.name}</span>
                    </Checkbox>
                    <div className="client-appointment-pay__item-row">
                      <div className="client-appointment-pay__qty">
                        <Button
                          size="small"
                          icon={<MinusOutlined />}
                          disabled={!canQty || qty <= 1 || submitting}
                          onClick={() => changeQty(item, -1)}
                        />
                        <span>{qty}</span>
                        <Button
                          size="small"
                          icon={<PlusOutlined />}
                          disabled={!canQty || qty >= item.qtyOrigin || submitting}
                          onClick={() => changeQty(item, 1)}
                        />
                      </div>
                      <b className={line < 0 ? "client-appointment-pay__sum--debt" : undefined}>
                        {formatMoney(line)}
                      </b>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="client-appointment-pay__empty">Нет неоплаченных услуг</div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default ClientAppointmentPayDrawer;
